import React, { useEffect, useContext, useState, useRef, useMemo, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import TopicsContext from "contexts/contextTopics";
import GlobalContext from "contexts/context";
import SubscriptionsContext from "contexts/contextSubscriptions";
import DatabaseContext from "data/contextDatabase";
import LiveCacheContext from "data/contextLiveCache";
import { isMobileTablet } from "hooks/helper";
import { WorldTextInput } from "utils/UtilsUniversal";
import Modals from "./Modals";
import "./modals.css";
import { dex_action } from "data/dexUtils";
import { useLiveQuery } from "dexie-react-hooks";

const UISearchMyTopicsModal = (props) => {
  const { topicsState, topicsDispatch } = useContext(TopicsContext);
  const { globalState, globalDispatch } = useContext(GlobalContext);
  const { subscriptionsState } = useContext(SubscriptionsContext);
  const { liveCacheState, liveCacheDispatch } = useContext(LiveCacheContext);
  const { databaseState } = useContext(DatabaseContext);
  const [modalClose, setModalClose] = useState(true);
  const [topicsList, setTopicsList] = useState([]);
  const [searchStr, setSearchStr] = useState();
  const displayList = useRef();
  const [focusVal, setFocusVal] = useState(true);
  // const [displayListNew, setDisplayListNew] = useState([]);
  let history = useHistory();
  const [includeBox, setIncludeBox] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [hiddenTopics, setHiddenTopics] = useState([]);
  const [archivedOnly, setArchivedOnly] = useState(false);
  // const isMounted = useRef(true);

  const liveHideArchived = useLiveQuery(
    () =>
      databaseState.dexUser &&
      databaseState.dexUser.account.get("hideArchived"),
    [databaseState.dexUser]
  );

  useEffect(() => {
    let isMounted = true;
    setIncludeBox(hiddenTopics?.length > 0 && liveHideArchived?.value === true);
    return () => {
      isMounted = false;
    };
  }, [liveHideArchived, hiddenTopics]);

  const goToTopic = useMemo(
    () => (t) => {
      toggleCollectionState(
        t.tag ||
          t.topic.props.tag ||
          t.topic.props.topiccollection ||
          t.topic.props.visibility ||
          t.topic.props.topictype ||
          "undefined",
        t.topic.mpersona
      );
      // we need this info to action the rest...
      let persona = {
        mpersona: t.topic.mpersona,
        persona: t.topic.persona,
        tag: (
          t.tag ||
          t.topic.props.tag ||
          t.topic.props.topiccollection ||
          t.topic.props.visibility ||
          t.topic.props.topictype ||
          "undefined"
        ).toLowerCase()
      };
      let collectionState = liveCacheState?.collectionState || {};
      process.env.REACT_APP_DEBUG_THIS &&
        console.log(
          "[UISearchMyTopicsModal] topic",
          t,
          "\n\tcollection",
          collectionState,
          "\n\tpersona:",
          persona
        );
      // make sure this topic's collection will be expanded
      if (
        collectionState &&
        collectionState[persona?.tag] &&
        collectionState[persona.tag][persona.mpersona]?.open &&
        collectionState[persona.tag][persona.mpersona].open !== false
      ) {
        collectionState[persona.tag][persona.mpersona].open = false;
        liveCacheDispatch({
          type: "SET",
          values: { cache: { collectionState: collectionState } }
        });
        databaseState.dexUser &&
          dex_action({
            type: "DEX_PUT",
            values: {
              db: databaseState.dexUser,
              table: "user",
              doc: { key: "collectionstate", value: collectionState }
            }
          });
      }
      // and open the topic
      if (t.mpersona !== globalState.persona?.mpersona) {
        globalDispatch({
          type: "SET_PERSONA",
          values: {
            persona: persona
          }
        });
      }
      if (isMobileTablet()) {
        let serializedObj = JSON.stringify({
          mtopic: t?.topic?.mtopic,
          mpersona: t?.topic?.mpersona
        });
        // sessionStorage.setItem("scrollMtopic", serializedObj);
        history.push("/UIMessageList", { subscription: t.topic });
      } else {
        // setModalClose(true);
        handleAdjPan({ subscription: t.topic });
      }
      setModalClose(true);
      displayList.current = undefined;
    },
    [
      liveCacheState?.collectionState,
      globalState.persona?.mpersona,
      databaseState.dexUser
    ]
  );

  // Use useMemo to derive displayListNew from topicsList and searchStr
  const displayListNew = useMemo(() => {
    if (!topicsList || topicsList.length === 0) {
      return [];
    }
    // If no search string, return all topics
    if (!searchStr || searchStr.length === 0) {
      return topicsList.map((item) => {
        return {
          topic_display_name: item.topic?.props?.topic_display_name,
          topic: item.topicName,
          persona: item.persona,
          description: item.topic?.props?.description,
          archived: item.topic?.subprops?.archived,
          onClick: () => goToTopic(item)
        };
      });
    }

    // Filter by search string
    return topicsList
      .filter((item) =>
        item.topicName?.toLowerCase().includes(searchStr.toLowerCase())
      )
      .map((item) => ({
        topic_display_name: item.topic?.props?.topic_display_name,
        topic: item.topicName,
        persona: item.persona,
        description: item.topic?.props?.description,
        archived: item.topic?.subprops?.archived,
        onClick: () => goToTopic(item)
      }));
  }, [topicsList, searchStr, goToTopic, archivedOnly]);

  const handleChange = (event) => {
    setIncludeHidden(event.target.checked);
  };

  useEffect(() => {
    let isMounted = true;
    setHiddenTopics([]);
    return () => {
      isMounted = false;
    };
  }, []);

  const rawHiddenTopics = useLiveQuery(() => {
    if (!databaseState.dexUser) return [];
    return databaseState.dexUser
      .table("account")
      .where("type")
      .equals("archivedTopics")
      .first()
      .then((doc) => doc?.value || []);
  }, [databaseState.dexUser, props.reloadKey]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted && rawHiddenTopics) {
      setHiddenTopics(rawHiddenTopics || []);
    }
    return () => {
      isMounted = false;
    };
  }, [rawHiddenTopics]);

  const handleClose = useCallback(async () => {
    try {
      // First, set local state to prevent further updates
      setModalClose(true);
      // Clear any references that might be used in async operations
      const currentDisplayList = displayList?.current;
      if (currentDisplayList) displayList.current = undefined;
      // Clear state that might trigger renders
      setHiddenTopics([]);
      // Finally call the parent's onClose which might unmount this component
      props.onClose();
    } catch (err) {
      console.error("Error during handleClose:", err);
    }
  }, [props]);

  const handleAdjPan = useCallback(
    (obj) => {
      topicsDispatch({
        type: "SET_SHOWTOPIC",
        values: { showTopic: obj }
      });
      // setModalClose(true);
      handleClose();
    },
    [topicsDispatch, handleClose]
  );

  useEffect(() => {
    let isMounted = true;
    if (
      sessionStorage.getItem("searchTopicString") &&
      sessionStorage.getItem("searchTopicString") !== undefined
    )
      setSearchStr(sessionStorage.getItem("searchTopicString"));
    else setSearchStr("");
    return () => {
      isMounted = false;
    };
  }, []);

  const handleFocus = (val) => {
    setFocusVal(val);
  };

  useEffect(() => {
    let isMounted = true;
    let shortList = [];

    if (topicsState?.topics) {
      Object.keys(topicsState.topics).forEach((t) => {
        const topic = topicsState.topics[t];
        if (topic.persona !== "unknown") {
          const topicName = topic.props.topic_display_name || topic.topic;
          shortList.push({
            topicName,
            persona: topic.persona,
            tag: topic.props?.tag || topic.props?.visibility,
            topic,
            description: props.description
          });
        }
      });
    }

    if (subscriptionsState?.subscriptions) {
      Object.keys(subscriptionsState.subscriptions).forEach((s) => {
        const sub = subscriptionsState.subscriptions[s];
        if (sub.persona !== "unknown") {
          const topicName = sub.topic_display_name || sub.topic;
          shortList.push({
            topicName,
            persona: sub.persona,
            tag: sub.visibility,
            topic: sub,
            description: sub
          });
        }
      });
    }

    // Add hidden topics if includeHidden is true
    if (includeHidden && hiddenTopics) {
      Object.keys(hiddenTopics).forEach((t) => {
        const ht = hiddenTopics[t];
        const topicName = ht.props?.topic_display_name || ht.topic;
        shortList.push({
          topicName,
          persona: ht.persona,
          tag: ht.props?.tag || ht.props?.visibility,
          topic: ht,
          description: ht
        });
      });
    }

    const sortedList = shortList.sort((a, b) =>
      a.topicName?.toLowerCase() > b.topicName?.toLowerCase() ? 1 : -1
    );

    setTopicsList(sortedList);
    return () => {
      isMounted = false;
    };
  }, [topicsState, subscriptionsState, includeHidden, hiddenTopics]);

  const toggleCollectionState = (topiccollection, mpersona) => {
    let collectionState = { ...liveCacheState?.collectionState } || {};
    collectionState[topiccollection] = collectionState[topiccollection] || {};
    collectionState[topiccollection][mpersona] = {
      open: !collectionState[topiccollection][mpersona]?.open
    };
    liveCacheDispatch({
      type: "SET",
      values: { cache: { collectionState: collectionState } }
    });
    databaseState.dexUser &&
      dex_action({
        type: "DEX_PUT",
        values: {
          db: databaseState.dexUser,
          table: "user",
          doc: { key: "collectionstate", value: collectionState }
        }
      });
  };

  useEffect(() => {
    window.addEventListener("popstate", handleClose);
    if (window.location?.href?.includes("search")) {
      window.history.replaceState({ id: 2 }, null, "/");
    } else {
      if (!modalClose) {
        window.history.pushState({ id: 2 }, null, "search");
      }
    }
    return () => {
      window.removeEventListener("popstate", handleClose);
    };
  }, [modalClose]);

  const handleClickOutside = () => {
    setModalClose(true);
    displayList.current = undefined;
  };

  // const handleClose = () => {
  //   try {
  //     props.onClose();
  //     setModalClose(true);
  //     setHiddenTopics([]);
  //     if (displayList?.current) displayList.current = undefined;
  //   } catch (err) {
  //     console.error("Error during handleClose:", err);
  //   }
  // };

  // const handleClose = () => {
  //   setModalClose(true);
  //   setHiddenTopics([]); // 🔄 clear the topics
  //   displayList.current = undefined;
  //   props.onClose();
  // };

  const handleSearchStr = (e) => {
    setSearchStr(e);
  };

  const handleClearContent = () => {
    setSearchStr("");
  };

  const chkCtrlF = useCallback((event) => {
    if (event.keyCode === 114 || (event.ctrlKey && event.keyCode === 70)) {
      event.preventDefault();
      setModalClose(false);
    }
  });

  // useEffect(() => {
  //   // Add listeners
  //   window.addEventListener("keydown", chkCtrlF);
  //   return () => {
  //     // Remove listeners
  //     window.removeEventListener("keydown", chkCtrlF);
  //   };
  // },[]);

  useEffect(() => {
    let isMounted = true;
    sessionStorage.setItem("searchTopicString", searchStr);
    return () => {
      isMounted = false;
    };
  }, [searchStr]);

  let content = (
    <div>
      <Modals
        title="Search for Topic"
        onClose={() => handleClose()}
        onClickOutside={() => handleClickOutside()}
        clickOutsideActive={true}
        switchScrollOff={true}
        noHistoryBack={true}
        style={{
          width: "500px",
          maxWidth: "80vw",
          maxHeight: "90vh"
        }}
      >
        <div
          className="UI-login-container"
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%", // use parent's maxHeight
            overflow: "hidden"
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobileTablet() ? "0rem" : "1rem",
              alignItems: "center"
            }}
          >
            {includeBox && (
              <label style={{ flexShrink: 0 }}>
                <input
                  className="checkbox"
                  type="checkbox"
                  checked={includeHidden}
                  onChange={handleChange}
                />
                Include hidden archived topics
              </label>
            )}
            {rawHiddenTopics &&
              rawHiddenTopics.length > 0 &&
              hiddenTopics &&
              hiddenTopics.length && (
                <label style={{ flexShrink: 0 }}>
                  <input
                    className="checkbox"
                    type="checkbox"
                    onChange={() => {
                      setArchivedOnly(!archivedOnly);
                    }}
                  />
                  Archived only
                </label>
              )}
          </div>
          <div
            style={{
              flexShrink: 0
              // marginBottom: "1rem"
            }}
          >
            <WorldTextInput
              // universalStyle={{ flexShrink: 0 }}
              // ref={ref}
              identity="searchtopic"
              // focus={focusVal}
              // removeFocus={(val) => handleFocus(val)}
              labelStyle={{
                fontWeight: "bold"
                // flexShrink: 0
              }}
              type="textWithClear"
              callback={(e) => handleSearchStr(e)}
              clearContent={() => handleClearContent()}
              label="Tap on topic to go there"
              placeholder="Searching for..."
              minRows={1}
              maxRows={1}
              value={searchStr}
              active="true"
              autoComplete="off"
            />
          </div>
          <div
            style={{
              flex: 1,
              overflow: "auto",
              minHeight: "0", // Necessary in some browsers to allow proper flex shrink
              paddingBottom: "1rem" // <-- Adds visual space or buffer
            }}
          >
            {displayListNew
              .filter(
                (item) =>
                  !archivedOnly ||
                  item.archived === true ||
                  item.archived === "true"
              )
              .map((item, index) => {
                const isHidden =
                  includeBox &&
                  hiddenTopics?.length > 0 &&
                  hiddenTopics.some(
                    (ht) =>
                      (ht.topic === item.topic ||
                        ht.props?.topic_display_name ===
                          item.topic_display_name) &&
                      ht.persona === item.persona
                    // );
                  );

                const isArchived =
                  item.archived === true || item.archived === "true";

                return (
                  <div
                    className="UI-topic-search-list"
                    style={{
                      gridTemplateColumns: isMobileTablet()
                        ? "1fr 1fr"
                        : "minmax(0, 5fr) 3fr 3fr",
                      gridTemplateRows: isMobileTablet()
                        ? "auto auto"
                        : "min-content",
                      columnGap: isMobileTablet() ? "0rem" : "0.5rem",
                      rowGap: isMobileTablet() ? "0.25rem" : "0.1rem",
                      fontSize: isMobileTablet() ? "0.8rem" : "1rem",
                      cursor: "pointer"
                    }}
                    key={index}
                    onClick={item.onClick}
                  >
                    <div
                      className="UI-topic-search-list-item"
                      style={{
                        fontWeight: "bold",
                        gridColumn: isMobileTablet() && "1/2"
                      }}
                    >
                      {item.topic_display_name || item.topic}
                    </div>
                    <div
                      className="UI-topic-search-list-item"
                      style={{
                        gridColumn: isMobileTablet() && "2/3"
                      }}
                    >
                      {item.persona}
                    </div>
                    <div
                      // className="UI-topic-search-list-hidden"
                      // className="UI-topic-meta"
                      style={{
                        display: isMobileTablet() ? "block" : "none"
                      }}
                    >
                      <span
                        className="UI-topic-search-list-description"
                        style={{
                          gridColumn: isMobileTablet() && "1/2"
                        }}
                      >
                        {item.description}
                      </span>
                    </div>
                    <div>
                      {isHidden && isArchived && (
                        <span
                          className="UI-topic-flag"
                          // style={{ color: "red", fontStyle: "italic" }}
                        >
                          Hidden{" "}
                        </span>
                      )}
                      {isArchived && (
                        <span
                          className="UI-topic-flag"
                          // style={{ color: "red", fontStyle: "italic" }}
                        >
                          Archived
                        </span>
                      )}
                    </div>
                    <div
                      className="UI-topic-search-list-description"
                      style={{ display: isMobileTablet() && "none" }}
                    >
                      {item.description}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </Modals>
    </div>
  );

  return content;
};

export default React.memo(UISearchMyTopicsModal, (prevProps, nextProps) => {
  return prevProps === nextProps;
});
